import dynamic from "next/dynamic"

const AppIcon = dynamic(() => import('@/app/components/atomic/AppIcon'))

interface Props {
    notation: number
    onlyFullStars?: boolean
    onlyOneStar?: boolean
    color?: 'yellow' | 'green' | 'blue' | 'darkblue'
    noteColorClass?: string
    notationOnTheLeft?: boolean
    displayRatingNotation?: boolean
    displayMaxNotation?: boolean
    starsFontSize?: string
    noteFontSize?: string
    gap?: string
}

export default function StarsRating({
    notation,
    onlyFullStars = false,
    onlyOneStar = false,
    color = 'yellow',
    displayRatingNotation = false,
    displayMaxNotation = false,
    notationOnTheLeft = false,
    starsFontSize = 'text-base',
    noteFontSize = 'text-base',
    noteColorClass = 'text-base',
    gap = 'gap-1.5',
}: Props) {
    const ratingOn5 = notation > 5 ? notation / 2 : notation
    const fullStars = Math.floor(ratingOn5)
    const decimalPart = ratingOn5 - fullStars

    let adjustedFullStars = fullStars
    let halfStars = 0

    if (decimalPart >= 0.75) {
        adjustedFullStars += 1
    } else if (decimalPart >= 0.25) {
        halfStars = 1
    }

    const emptyStars = 5 - (adjustedFullStars + halfStars)

    const colorClass =
        color === 'yellow'
            ? 'text-yellow-700'
            : color === 'green'
                ? 'text-pastel-green'
                : color === 'darkblue'
                    ? 'text-blue-900'
                : 'text-blue-500'

    if (onlyOneStar) {
        adjustedFullStars = 1
        halfStars = 0
    }

    const notationFormatted = ratingOn5.toString().replace('.', ',')

    return (
        <div className={`inline-flex items-center h-4 ${gap}`}>
            {displayRatingNotation && notationOnTheLeft &&
                <div className={`whitespace-nowrap font-semibold pt-0.5 mr-1 ${noteFontSize} ${noteColorClass}`}>
                    {notationFormatted}{displayMaxNotation && <span className='text-[0.6rem]'> / 5</span>}
                </div>
            }

            {!onlyFullStars ? (
                <>
                    {Array.from({length: adjustedFullStars}, (_, i) => (
                        <AppIcon
                            key={i}
                            type="solid"
                            iconClassName={`fa-star ${colorClass} ${starsFontSize}`}
                        />
                    ))}

                    {halfStars === 1 && (
                        <AppIcon
                            type="solid"
                            iconClassName={`fa-star-half-stroke ${colorClass} ${starsFontSize}`}
                        />
                    )}

                    {Array.from({length: emptyStars}, (_, i) => (
                        <AppIcon
                            key={i}
                            type="regular"
                            iconClassName={`fa-regular fa-star ${colorClass} ${starsFontSize}`}
                        />
                    ))}
                </>
            ) : (
                <>
                    {Array.from({length: adjustedFullStars + (halfStars ? 1 : 0)}, (_, i) => (
                        <AppIcon
                            key={i}
                            type="solid"
                            iconClassName={`fa-star ${colorClass} ${starsFontSize}`}
                        />
                    ))}

                    {Array.from({length: emptyStars}, (_, i) => (
                        <AppIcon
                            key={i}
                            type="regular"
                            iconClassName={`fa-regular fa-star ${colorClass} ${starsFontSize}`}
                        />
                    ))}
                </>
            )}

            {displayRatingNotation && !notationOnTheLeft &&
                <div className={`font-semibold pt-0.5 ${noteFontSize} ${noteColorClass}`}>
                    {notationFormatted}{displayMaxNotation && <span className='text-[0.6rem]'> / 5</span>}
                </div>
            }
        </div>
    )
}
